import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaskIcon } from '../components/Icon';
import { t } from '@alterrego/core-i18n';
import { isCurrentUserAdmin } from '../utils/auth';
import { apiFetch } from '../utils/api';
import './CaveScreen.css';

interface Wine {
  id: number;
  name: string;
  producer: string | null;
  appellation: string | null;
  region: string | null;
  country: string | null;
  vintage: number | null;
  type: string | null;
  color: string | null;
  alcohol: number | null;
  avg_price_eur: number | null;
  tasting_notes: string | null;
}

function getUser() {
  try {
    const raw = localStorage.getItem('alterrego_user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}


type Filter = 'all' | 'rouge' | 'blanc' | 'spirit';

export function CaveScreen() {
  const navigate = useNavigate();
  const [wines, setWines] = useState<Wine[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const admin = useMemo(() => isCurrentUserAdmin(), []);

  const fetchWines = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const qs = search ? `?q=${encodeURIComponent(search)}&limit=200` : '?limit=200';
      const res = await apiFetch(`/cave/wines${qs}`, { auth: false });
      if (res.ok) {
        const data = await res.json();
        setWines(data.results ?? []);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchWines(); }, [fetchWines]);

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => fetchWines(query || undefined), 300);
    return () => clearTimeout(id);
  }, [query, fetchWines]);

  const filtered = useMemo(() => {
    if (filter === 'all') return wines;
    if (filter === 'spirit') return wines.filter((w) => w.type?.startsWith('spirit:'));
    return wines.filter((w) => {
      const color = (w.color ?? w.type ?? '').toLowerCase();
      if (filter === 'rouge') return color.includes('rouge') || color === 'red';
      if (filter === 'blanc') return color.includes('blanc') || color === 'white' || color.includes('jaune');
      return true;
    });
  }, [wines, filter]);

  const typeLabel = (w: Wine) => {
    if (w.type?.startsWith('spirit:')) return w.type.replace('spirit:', '').charAt(0).toUpperCase() + w.type.replace('spirit:', '').slice(1);
    return w.type ?? '';
  };

  const colorClass = (w: Wine) => {
    if (w.type?.startsWith('spirit:')) return 'spirit';
    const c = (w.color ?? '').toLowerCase();
    if (c.includes('rouge') || c.includes('red')) return 'rouge';
    if (c.includes('blanc') || c.includes('white') || c.includes('jaune')) return 'blanc';
    if (c.includes('ros')) return 'rose';
    return 'other';
  };

  return (
    <div className="cave">
      <div className="cave-header">
        <h1>{t('cave_titre')}</h1>
        <p>{wines.length} references</p>
      </div>

      {/* Filters */}
      <div className="cave-filters">
        {([['all', 'Tout'], ['rouge', '🍷 Rouges'], ['blanc', '🥂 Blancs'], ['spirit', '🥃 Spiritueux']] as const).map(([k, label]) => (
          <button key={k} className={`cave-filter ${filter === k ? 'active' : ''}`}
            onClick={() => setFilter(k as Filter)}>
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="cave-search">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('cave_rechercher')} />
      </div>

      {/* List */}
      <div className="cave-list">
        {loading && wines.length === 0 ? (
          <div className="cave-empty"><p>Chargement...</p></div>
        ) : filtered.length === 0 ? (
          <div className="cave-empty">
            <p>{query ? 'Aucun resultat' : t('cave_aucun')}</p>
          </div>
        ) : (
          filtered.map((w) => (
            <div key={w.id} className={`cave-item ${expandedId === w.id ? 'expanded' : ''}`}
              onClick={() => setExpandedId(expandedId === w.id ? null : w.id)}>
              <div className="cave-item-row">
                <div className={`cave-color-dot ${colorClass(w)}`} />
                <div className="cave-item-info">
                  <strong>{w.name}</strong>
                  <span>
                    {w.producer ?? ''}{w.vintage ? ` · ${w.vintage}` : ''}{w.region ? ` · ${w.region}` : ''}
                  </span>
                </div>
                {w.avg_price_eur && <span className="cave-price">{w.avg_price_eur}€</span>}
              </div>

              {expandedId === w.id && (
                <div className="cave-detail">
                  {w.appellation && <div className="cave-detail-row"><span>Appellation</span><strong>{w.appellation}</strong></div>}
                  {w.alcohol && <div className="cave-detail-row"><span>Alcool</span><strong>{w.alcohol}%</strong></div>}
                  {typeLabel(w) && <div className="cave-detail-row"><span>Type</span><strong>{typeLabel(w)}</strong></div>}
                  {w.tasting_notes && <p className="cave-tasting">{w.tasting_notes}</p>}
                  <button className="cave-detail-btn" onClick={(e) => { e.stopPropagation(); navigate(`/cave/${w.id}`); }}>
                    Voir la fiche complete
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* FAB ajout — admin seulement */}
      {admin && (
        <button className="cave-fab" onClick={() => navigate('/scan')}>
          <MaskIcon name="qrcode" size={22} color="white" /> Scan
        </button>
      )}
    </div>
  );
}
